import React, { useState } from "react";
import { Calendar, ChevronDown, ChevronUp } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { CardActions } from "./CardActions";
import { getInitials } from "@/utils/mentorUtils";
import { MentorshipOfferingProps } from "./types";

interface CardContentProps extends MentorshipOfferingProps {
  handleScheduleClick: (e: React.MouseEvent) => void;
}

export const CardContent: React.FC<CardContentProps> = ({
  description,
  duration,
  game,
  mentorName,
  mentorImageUrl,
  mentorId,
  isOwner,
  isActive,
  id,
  price,
  onEdit,
  onDelete,
  handleScheduleClick
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Get mentor initials for avatar fallback
  const initials = getInitials(mentorName || '');

  // Ensure display name is never empty
  const displayName = mentorName && mentorName.trim() !== '' 
    ? mentorName 
    : (mentorId ? `Mentor ${mentorId.substring(0, 4)}` : `Mentor ${id.substring(0, 4)}`);

  // Format price
  const formattedPrice = `R$ ${(price / 100).toFixed(2).replace(".", ",")}`;

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Função para truncar a descrição
  const getShortDescription = (text: string, maxLength: number = 80) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="relative px-5 pt-5 pb-4">
      {/* Mentor Image & Name */}
      <div className="flex items-center mb-3">
        <Avatar className="w-10 h-10 border-2 border-[#00B6F0] mr-3">
          <AvatarImage src={mentorImageUrl || undefined} alt={displayName} />
          <AvatarFallback className="bg-[#223342] text-[#00B6F0]">{initials || "MT"}</AvatarFallback>
        </Avatar>
        <span className="font-bold text-[#00B6F0]">{displayName}</span>
      </div>
      
      {/* Game */}
      {game && (
        <div className="text-sm text-[#00B6F0] mb-2">
          {game.name}
        </div>
      )}
      
      {/* Description */}
      <div className="mb-3">
        <p className="text-gray-300 text-sm">
          {isExpanded ? description : getShortDescription(description)}
        </p>
        {description.length > 80 && (
          <button
            onClick={toggleExpanded}
            className="text-[#00B6F0] text-xs hover:text-[#00C7FF] transition-colors mt-1 flex items-center gap-1"
          >
            {isExpanded ? (
              <>
                Ver menos <ChevronUp className="w-3 h-3" />
              </>
            ) : (
              <>
                Ver mais <ChevronDown className="w-3 h-3" />
              </>
            )}
          </button>
        )}
      </div>
      
      {/* Duration */}
      <div className="text-gray-400 text-sm mb-3">
        Duração: <span className="text-white">{duration} minutos</span>
      </div>

      {/* Price */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-2xl font-bold text-[#00B6F0]">
          {formattedPrice}
        </div>
      </div>

      {/* Actions */}
      <CardActions
        isOwner={isOwner}
        isActive={isActive}
        onEdit={() => onEdit?.(id)}
        onDelete={() => onDelete?.(id)}
        onSchedule={handleScheduleClick}
      />
    </div>
  );
};
